/**
 * useBatchQueries - Data fetching for batch form
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBatchConfig } from '@/lib/api/batch';
import { getWorkspacePresets } from '@/lib/api/workspace';
import { listCollections, getCollection } from '@/lib/api/template-collections';
import type { BatchMode } from '@/lib/api/batch';

export function useBatchQueries(mode: BatchMode, collectionId: string) {
  const { data: config } = useQuery({ queryKey: ['batch', 'config'], queryFn: getBatchConfig });
  const { data: presetsData } = useQuery({ queryKey: ['workspace', 'presets'], queryFn: getWorkspacePresets });
  const { data: collectionsData } = useQuery({
    queryKey: ['template-collections'],
    queryFn: () => listCollections({ page_size: 100 }),
  });
  const { data: selectedCollection } = useQuery({
    queryKey: ['template-collection', collectionId],
    queryFn: () => getCollection(collectionId),
    enabled: mode === 'collection' && !!collectionId,
  });

  const templates = presetsData?.presets || [];
  const collections = collectionsData?.collections || [];

  // Batch costs from batch config endpoint (fetched from feature_catalog)
  const discount = config?.user_discount_percent ?? 0;
  const costPerImage = config?.cost_per_image ?? 0;
  const costPerImage4k = config?.cost_per_image_4k ?? costPerImage;

  const collectionImageCount = useMemo(() => {
    if (!selectedCollection?.items) return 0;
    return selectedCollection.items.reduce((sum, item) => sum + item.image_count, 0);
  }, [selectedCollection]);

  return { templates, collections, discount, costPerImage, costPerImage4k, collectionImageCount };
}
